import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCivilDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import { creditSimulator } from './api';
import { ExposicaoFicticia } from './ExposicaoFicticia';
import {
  estadoInicial,
  linhaVazia,
  montarPedido,
  origensPermitidas,
  textoDaAlteracao,
  tipoDaAlteracao,
  type Alteracao,
  type EstadoDoSimulador,
  type LinhaDeDado,
} from './formulario';
import { modeloVigidata, modelosVigidata } from './modelosVigidata';
import { ResultadoDaSimulacao } from './ResultadoDaSimulacao';
import {
  AVISO_DA_TELA,
  estadosDoDado,
  origens,
  PERMISSAO_SIMULAR,
  produtosDaVigidata,
  situacoesDaConsulta,
} from './rotulos';
import type {
  DataStateView,
  ElementSpec,
  ParameterSetSummary,
  Simulation,
  VigidataProduct,
} from './types';

/**
 * Simulador de regras de credito (ADR 0083), em Configuracoes > Analise de credito.
 *
 * A pessoa testa uma alteracao de regra antes de aplica-la: escolhe a versao de
 * parametros ou monta alteracoes pontuais, informa dados ficticios do tomador com a
 * origem e a data de referencia de cada um, uma resposta ficticia da Vigidata e a
 * operacao. O servidor roda o mesmo motor das analises reais e nao grava nada.
 */
export function CreditSimulatorPage() {
  const { can, session } = useAuth();
  const permitido = can(PERMISSAO_SIMULAR) && !session?.broker_scoped;

  if (!permitido) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Simulador de regras de credito" />
        <Alert tone="warning" title="Sem acesso ao simulador">
          O simulador exige a permissao de visualizar os parametros do motor de credito
          ({PERMISSAO_SIMULAR}) e e da seguradora: o acesso pela corretora nao simula.
        </Alert>
      </div>
    );
  }
  return <Simulador />;
}

function Simulador() {
  const [estado, setEstado] = useState<EstadoDoSimulador>(estadoInicial);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [chaveNova, setChaveNova] = useState('');

  const catalogo = useQuery({
    queryKey: ['credit-simulator', 'catalog'],
    queryFn: ({ signal }) => creditSimulator.catalog(signal),
  });
  const versoes = useQuery({
    queryKey: ['credit-simulator', 'parameter-sets'],
    queryFn: ({ signal }) => creditSimulator.parameterSets(signal),
  });
  const modalidades = useQuery({
    queryKey: ['credit-simulator', 'modalities'],
    queryFn: ({ signal }) => creditSimulator.modalities(signal),
    retry: false,
  });

  const ativa = versoes.data?.find((v) => v.status === 'ativa');
  const baseId = estado.versaoId || ativa?.id || '';
  const base = useQuery({
    queryKey: ['credit-simulator', 'parameter-set', baseId],
    queryFn: ({ signal }) => creditSimulator.parameterSet(baseId, signal),
    enabled: baseId !== '',
  });

  const simular = useMutation({
    mutationFn: creditSimulator.simulate,
  });
  const simulacao: Simulation | undefined = simular.data;
  const estadosLidos = new Map<string, DataStateView>(
    (simulacao?.data_states ?? []).map((s) => [s.element, s]),
  );

  const apiError = simular.error instanceof ApiError ? simular.error : null;
  const campoDoErro =
    typeof apiError?.details?.field === 'string' ? apiError.details.field : undefined;

  function atualizar(parcial: Partial<EstadoDoSimulador>) {
    setEstado((atual) => ({ ...atual, ...parcial }));
  }

  function atualizarDado(chave: string, parcial: Partial<LinhaDeDado>) {
    setEstado((atual) => ({
      ...atual,
      dados: {
        ...atual.dados,
        [chave]: { ...(atual.dados[chave] ?? linhaVazia()), ...parcial },
      },
    }));
  }

  function atualizarAlteracao(chave: string, texto: string) {
    setEstado((atual) => ({
      ...atual,
      alteracoes: atual.alteracoes.map((a) =>
        a.chave === chave ? { ...a, texto } : a,
      ),
    }));
  }

  function adicionarAlteracao() {
    if (chaveNova === '' || estado.alteracoes.some((a) => a.chave === chaveNova))
      return;
    const atual = base.data?.values?.[chaveNova];
    const alteracao: Alteracao = {
      chave: chaveNova,
      tipo: tipoDaAlteracao(atual),
      texto: textoDaAlteracao(atual),
    };
    atualizar({ alteracoes: [...estado.alteracoes, alteracao] });
    setChaveNova('');
  }

  function usarModelo(tipo: (typeof modelosVigidata)[number]['chave']) {
    atualizar({
      bureauLigado: true,
      produto: 'analise_credito_cnpj',
      respostaTexto: modeloVigidata(tipo, estado.documento),
    });
  }

  function enviar(event: FormEvent) {
    event.preventDefault();
    const { pedido, erros: novos } = montarPedido(
      estado,
      catalogo.data?.elements ?? [],
    );
    setErros(novos);
    if (pedido) simular.mutate(pedido);
  }

  const opcoesDeVersao = [
    {
      value: '',
      label: ativa
        ? `Versao ativa ${ativa.version}`
        : 'Sem versao ativa: valores documentados',
    },
    ...(versoes.data ?? [])
      .filter((v) => v.status === 'rascunho' || v.status === 'agendada')
      .map((v: ParameterSetSummary) => ({
        value: v.id,
        label:
          v.status === 'agendada'
            ? `Agendada ${v.version}${v.effective_from ? ` (vigencia ${formatCivilDate(v.effective_from.slice(0, 10))})` : ''}`
            : `Rascunho ${v.version}`,
      })),
  ];
  const chavesDosParametros = Object.keys(base.data?.values ?? {}).sort();

  return (
    <form className="flex flex-col gap-6" onSubmit={enviar} noValidate>
      <PageHeader
        title="Simulador de regras de credito"
        description="Teste uma alteracao de regra antes de aplica-la, com o mesmo motor das analises reais."
        meta={<Badge tone="warning">Simulacao: nada e gravado</Badge>}
      />
      <Alert tone="info">{AVISO_DA_TELA}</Alert>

      <Card>
        <CardHeader
          title="Versao de parametros"
          description="A versao escolhida e avaliada como se estivesse em vigor."
        />
        <CardBody className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Versao simulada"
              options={opcoesDeVersao}
              value={estado.versaoId}
              error={campoDoErro === 'parameter_set_id' ? apiError?.message : undefined}
              onChange={(event) =>
                atualizar({ versaoId: event.target.value, alteracoes: [] })
              }
            />
            <label className="flex items-start gap-2 pt-6 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={estado.comoHomologada}
                onChange={(event) =>
                  atualizar({ comoHomologada: event.target.checked })
                }
              />
              <span>
                Simular como homologada
                <span className="block text-xs text-ink-500">
                  Todas as regras decidem, inclusive as que estao em sombra. Sem isto,
                  so decidem as regras de regras_homologadas, como na execucao real.
                </span>
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold text-ink-700">
              Alteracoes pontuais (nao gravadas)
            </p>
            {baseId === '' ? (
              <p className="text-xs text-ink-500">
                Sem versao ativa: escolha um rascunho para listar e alterar os
                parametros.
              </p>
            ) : null}
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-64">
                <Select
                  label="Parametro"
                  placeholder="Escolha o parametro"
                  options={chavesDosParametros.map((chave) => ({
                    value: chave,
                    label: chave,
                  }))}
                  value={chaveNova}
                  onChange={(event) => setChaveNova(event.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                onClick={adicionarAlteracao}
                disabled={chaveNova === ''}
              >
                Adicionar alteracao
              </Button>
            </div>
            {estado.alteracoes.map((alteracao) => {
              const erro =
                erros[`alteracao.${alteracao.chave}`] ??
                (campoDoErro === `parameters.${alteracao.chave}`
                  ? apiError?.message
                  : undefined);
              return (
                <div key={alteracao.chave} className="flex flex-wrap items-start gap-2">
                  <div className="min-w-64 flex-1">
                    {alteracao.tipo === 'json' ? (
                      <Textarea
                        label={alteracao.chave}
                        help={false}
                        value={alteracao.texto}
                        error={erro}
                        hint="JSON com os nomes da especificacao 41. Numeros inteiros: dinheiro em centavos, fator em milesimos."
                        onChange={(event) =>
                          atualizarAlteracao(alteracao.chave, event.target.value)
                        }
                      />
                    ) : alteracao.tipo === 'booleano' ? (
                      <Select
                        label={alteracao.chave}
                        help={false}
                        options={[
                          { value: 'sim', label: 'Sim' },
                          { value: 'nao', label: 'Nao' },
                        ]}
                        value={alteracao.texto}
                        error={erro}
                        onChange={(event) =>
                          atualizarAlteracao(alteracao.chave, event.target.value)
                        }
                      />
                    ) : alteracao.tipo === 'dinheiro' ? (
                      <MoneyField
                        label={`${alteracao.chave} (R$)`}
                        help={false}
                        value={alteracao.texto}
                        error={erro}
                        hint="Em branco e nulo (em branco)."
                        onValueChange={(texto) =>
                          atualizarAlteracao(alteracao.chave, texto)
                        }
                      />
                    ) : (
                      <Field
                        label={alteracao.chave}
                        help={false}
                        inputMode="numeric"
                        value={alteracao.texto}
                        error={erro}
                        onChange={(event) =>
                          atualizarAlteracao(alteracao.chave, event.target.value)
                        }
                      />
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    className="mt-6"
                    onClick={() =>
                      atualizar({
                        alteracoes: estado.alteracoes.filter(
                          (a) => a.chave !== alteracao.chave,
                        ),
                      })
                    }
                  >
                    Remover
                  </Button>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Dados ficticios do tomador"
          description="Cada dado com a origem e a data de referencia: e o que decide o estado do dado na matriz (PM-02)."
        />
        <CardBody className="flex flex-col gap-4">
          <div className="max-w-sm">
            <Field
              label="CNPJ do tomador ficticio"
              help="Documento ficticio. A resposta da SeguraCoreCredit precisa trazer o mesmo documento."
              value={estado.documento}
              error={
                campoDoErro === 'policyholder.document' ? apiError?.message : undefined
              }
              onChange={(event) => atualizar({ documento: event.target.value })}
            />
          </div>
          {catalogo.isLoading ? (
            <p className="text-sm text-ink-500">Carregando os elementos...</p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-ink-50 text-ink-600">
                <tr>
                  <th className="px-2 py-2">Elemento</th>
                  <th className="px-2 py-2">Origem</th>
                  <th className="px-2 py-2">Valor</th>
                  <th className="px-2 py-2">Data de referencia</th>
                  <th className="px-2 py-2">Consulta</th>
                  <th className="px-2 py-2">Estado lido pelo motor</th>
                </tr>
              </thead>
              <tbody>
                {(catalogo.data?.elements ?? []).map((spec) => (
                  <LinhaDoElemento
                    key={spec.key}
                    spec={spec}
                    linha={estado.dados[spec.key] ?? linhaVazia()}
                    erro={erros[`dado.${spec.key}`]}
                    lido={estadosLidos.get(spec.key)}
                    onChange={(parcial) => atualizarDado(spec.key, parcial)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Consulta ficticia ao bureau"
          description="Nada e consultado na SeguraCoreCredit. A resposta colada e lida pela mesma leitura do motor."
        />
        <CardBody className="flex flex-col gap-4">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={estado.bureauLigado}
              onChange={(event) => atualizar({ bureauLigado: event.target.checked })}
            />
            Incluir a consulta ficticia (a data e a resposta abaixo, e os dados com
            origem consulta)
          </label>
          {estado.bureauLigado ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <Select
                  label="Produto da SeguraCoreCredit"
                  options={produtosDaVigidata}
                  value={estado.produto}
                  onChange={(event) =>
                    atualizar({ produto: event.target.value as VigidataProduct })
                  }
                />
                <Field
                  label="Data da consulta ficticia"
                  type="date"
                  value={estado.dataConsulta}
                  hint="Em branco, a consulta e de agora."
                  error={
                    erros.dataConsulta ??
                    (campoDoErro === 'bureau.consulted_at'
                      ? apiError?.message
                      : undefined)
                  }
                  onChange={(event) => atualizar({ dataConsulta: event.target.value })}
                />
                <label className="flex items-start gap-2 pt-6 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={estado.provedorFicticio}
                    onChange={(event) =>
                      atualizar({ provedorFicticio: event.target.checked })
                    }
                  />
                  <span>
                    Consulta de provedor ficticio
                    <span className="block text-xs text-ink-500">
                      Liga a trava SIMULADO do motor: o dado nunca libera regra.
                    </span>
                  </span>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {modelosVigidata.map((modelo) => (
                  <Button
                    key={modelo.chave}
                    variant="secondary"
                    size="sm"
                    title={modelo.descricao}
                    onClick={() => usarModelo(modelo.chave)}
                  >
                    Modelo: {modelo.rotulo}
                  </Button>
                ))}
              </div>
              <Textarea
                label="Resposta ficticia do fornecedor (JSON)"
                rows={10}
                value={estado.respostaTexto}
                hint="Cole uma resposta no formato da SeguraCoreCredit ou use um modelo. O texto vai como esta, sem converter numero."
                error={
                  erros.resposta ??
                  (campoDoErro?.startsWith('bureau.') ? apiError?.message : undefined)
                }
                onChange={(event) => atualizar({ respostaTexto: event.target.value })}
              />
            </>
          ) : null}
        </CardBody>
      </Card>

      <ExposicaoFicticia
        estado={estado.exposicao}
        erros={erros}
        erroDoServidor={
          campoDoErro?.startsWith('exposure.') ? apiError?.message : undefined
        }
        onChange={(exposicao) => atualizar({ exposicao })}
      />

      <Card>
        <CardHeader title="Operacao ficticia" />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Modalidade da empresa"
            placeholder={
              modalidades.isError
                ? 'Cadastro de produtos indisponivel'
                : 'Usar a referencia'
            }
            options={(modalidades.data ?? []).map((m) => ({
              value: m.id,
              label: `${m.name} (${m.credit_group ?? 'sem grupo de credito'})`,
            }))}
            value={estado.modalidadeId}
            error={
              erros.modalidade ??
              (campoDoErro === 'operation.modality_id' ? apiError?.message : undefined)
            }
            onChange={(event) => atualizar({ modalidadeId: event.target.value })}
          />
          <Select
            label="Referencia de modalidade"
            placeholder="Escolha a referencia"
            disabled={estado.modalidadeId !== ''}
            options={(catalogo.data?.modality_references ?? []).map((r) => ({
              value: r.key,
              label: `${r.label} (${r.group})`,
            }))}
            value={estado.referencia}
            error={
              campoDoErro === 'operation.reference' || campoDoErro === 'operation.group'
                ? apiError?.message
                : undefined
            }
            onChange={(event) => atualizar({ referencia: event.target.value })}
          />
          <MoneyField
            label="Importancia segurada (R$)"
            value={estado.importanciaSegurada}
            error={
              erros.importanciaSegurada ??
              (campoDoErro === 'operation.insured_amount'
                ? apiError?.message
                : undefined)
            }
            onValueChange={(texto) => atualizar({ importanciaSegurada: texto })}
          />
        </CardBody>
      </Card>

      {apiError && !campoDoErro ? (
        <Alert tone="danger" title="A simulacao nao foi feita">
          {apiError.message}
        </Alert>
      ) : null}
      {apiError && campoDoErro ? (
        <Alert tone="danger" title="Corrija o campo indicado">
          {apiError.message} ({campoDoErro})
        </Alert>
      ) : null}

      <div>
        <Button type="submit" loading={simular.isPending} disabled={catalogo.isLoading}>
          Simular
        </Button>
      </div>

      {simulacao ? <ResultadoDaSimulacao simulation={simulacao} /> : null}
    </form>
  );
}

function LinhaDoElemento({
  spec,
  linha,
  erro,
  lido,
  onChange,
}: {
  spec: ElementSpec;
  linha: LinhaDeDado;
  erro: string | undefined;
  lido: DataStateView | undefined;
  onChange: (parcial: Partial<LinhaDeDado>) => void;
}) {
  const informado = linha.origem !== '';
  const comData = linha.origem === 'documento' || linha.origem === 'consulta';
  const deConsulta = linha.origem === 'consulta';
  const rotuloDoValor =
    spec.kind === 'dinheiro' && !spec.label.includes('(R$)')
      ? `${spec.label} (R$)`
      : spec.label;

  return (
    <tr className="border-t border-line align-top">
      <td className="px-2 py-2">
        <span className="font-semibold text-ink-900">{spec.label}</span>
        <span className="block text-ink-500">
          {spec.key}
          {spec.de ? ` (${spec.de})` : ''}
        </span>
      </td>
      <td className="px-2 py-2">
        <Select
          label={`${spec.label}: origem`}
          labelHidden
          options={[
            { value: '', label: 'Nao informado' },
            ...origensPermitidas(spec).map((o) => ({ value: o, label: origens[o] })),
          ]}
          value={linha.origem}
          onChange={(event) =>
            onChange({ origem: event.target.value as LinhaDeDado['origem'] })
          }
        />
      </td>
      <td className="px-2 py-2">
        {!informado ? null : spec.kind === 'indicador' ? (
          <Select
            label={`${rotuloDoValor}: valor`}
            labelHidden
            placeholder="Sem valor"
            options={[
              { value: 'sim', label: 'Sim' },
              { value: 'nao', label: 'Nao' },
            ]}
            value={linha.valor}
            error={erro}
            onChange={(event) => onChange({ valor: event.target.value })}
          />
        ) : spec.allowed && spec.allowed.length > 0 ? (
          <Select
            label={`${rotuloDoValor}: valor`}
            labelHidden
            placeholder="Sem valor"
            options={spec.allowed.map((v) => ({ value: v, label: v }))}
            value={linha.valor}
            error={erro}
            onChange={(event) => onChange({ valor: event.target.value })}
          />
        ) : spec.kind === 'dinheiro' ? (
          <MoneyField
            label={`${rotuloDoValor}: valor`}
            labelHidden
            value={linha.valor}
            error={erro}
            onValueChange={(texto) => onChange({ valor: texto })}
          />
        ) : (
          <Field
            label={`${rotuloDoValor}: valor`}
            labelHidden
            type={spec.kind === 'data' ? 'date' : 'text'}
            inputMode={spec.kind === 'inteiro' ? 'numeric' : undefined}
            placeholder="Sem valor"
            value={linha.valor}
            error={erro}
            onChange={(event) => onChange({ valor: event.target.value })}
          />
        )}
      </td>
      <td className="px-2 py-2">
        {comData ? (
          <Field
            label={`${spec.label}: data de referencia`}
            labelHidden
            type="date"
            value={linha.dataReferencia}
            onChange={(event) => onChange({ dataReferencia: event.target.value })}
          />
        ) : null}
      </td>
      <td className="px-2 py-2">
        {deConsulta ? (
          <div className="flex flex-col gap-1">
            <Select
              label={`${spec.label}: situacao na consulta`}
              labelHidden
              placeholder="Encontrado (com valor)"
              options={situacoesDaConsulta}
              value={linha.situacao}
              onChange={(event) =>
                onChange({ situacao: event.target.value as LinhaDeDado['situacao'] })
              }
            />
            {spec.kind === 'inteiro' ? (
              <Field
                label={`${spec.label}: modelo do score`}
                labelHidden
                placeholder="Modelo do score"
                value={linha.modelo}
                onChange={(event) => onChange({ modelo: event.target.value })}
              />
            ) : null}
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {lido ? (
          <Badge
            tone={
              lido.state === 'verificado'
                ? 'success'
                : lido.state === 'digitado'
                  ? 'info'
                  : 'warning'
            }
          >
            {estadosDoDado[lido.state] ?? lido.state}
          </Badge>
        ) : (
          <span className="text-ink-400">-</span>
        )}
      </td>
    </tr>
  );
}
